import React, {useContext, useEffect, useRef, useState} from "react";
import styles from "../../../styles/components/Modal/LeadSettings/AddLeadModal.module.css";
import CloseIcon from "@mui/icons-material/Close";
import {Notification} from "../../UI/Notifications";
import {LoadingCircle} from "../../UI/Custom/LoadingCircle";
import { DropdownSelector } from "../../UI/DropdownSelector";

import {getAdmins} from "../../../utils/api/Admin/getAdminsApi";
import {getAllDesksApi} from "../../../utils/api/Admin/desk/getAllDesksApi";
import {getAffiliate} from "../../../utils/api/Affiliate/getAffiliateApi";
import {handleError} from "../../../utils/custom/errorHandler";
import {LangContext} from "../../../context/LangContext";
import { mapGeoOptions } from "../../../utils/geoCountryNames";

const API_BASE_URL = process.env.REACT_APP_BASE_API_URL;

const translations = {
    ru: {
        title: "Импорт лидов",
        fileLabel: "Файл (CSV, XLSX, XLS)",
        managerLabel: "Менеджер (по умолчанию)",
        deskLabel: "Деск (по умолчанию)",
        affiliatorLabel: "Аффилиат (по умолчанию)",
        saleStatusLabel: "Sale статус (по умолчанию)",
        retenStatusLabel: "Retention статус (по умолчанию)",
        geoLabel: "GEO (по умолчанию)",
        submit: "Импортировать",
        submitting: "Импортируем...",
        fileRequired: "Пожалуйста, выберите файл для импорта.",
        selectPlaceholder: "Не выбрано",
    },
    en: {
        title: "Import leads",
        fileLabel: "File (CSV, XLSX, XLS)",
        managerLabel: "Manager (default)",
        deskLabel: "Desk (default)",
        affiliatorLabel: "Affiliator (default)",
        saleStatusLabel: "Sale status (default)",
        retenStatusLabel: "Retention status (default)",
        geoLabel: "GEO (default)",
        submit: "Import",
        submitting: "Importing...",
        fileRequired: "Please select a file to import.",
        selectPlaceholder: "Not selected",
    },
};

export const ImportLeadsModal = ({ onClose, onSuccess }) => {
    const { langSelected } = useContext(LangContext);
    const t = translations[langSelected] || translations.ru;

    const modalRef = useRef(null);

    const [file, setFile] = useState(null);
    const [formData, setFormData] = useState({
        manager_id: "",
        desk_id: "",
        affiliator_id: "",
        sale_status: "",
        reten_status: "",
        geo: "",
    });

    const [managerOptions, setManagerOptions] = useState([]);
    const [deskOptions, setDeskOptions] = useState([]);
    const [affiliatorOptions, setAffiliatorOptions] = useState([]);
    const [saleStatusOptions, setSaleStatusOptions] = useState([]);
    const [retenStatusOptions, setRetenStatusOptions] = useState([]);
    const [geoOptions, setGeoOptions] = useState([]);

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleFieldChange = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const getToken = () => localStorage.getItem("token");

    const normalizeList = (json) => {
        if (Array.isArray(json)) return json;
        if (json && Array.isArray(json.data)) return json.data;
        if (json && Array.isArray(json.items)) return json.items;
        return [];
    };

    const mapToOptions = (items) => {
        return (items || [])
            .map((it) => {
                if (typeof it === "string" || typeof it === "number") {
                    const v = String(it).trim();
                    if (!v) return null;
                    return { value: v, label: v };
                }

                if (it && typeof it === "object") {
                    const value = it.value ?? it.id ?? it.key ?? it.code ?? it.name;
                    const label =
                        it.label ?? it.name ?? it.title ?? (value != null ? String(value) : "");

                    if (value === undefined || value === null || String(value).trim() === "") {
                        return null;
                    }
                    return { value: String(value), label: String(label) };
                }

                return null;
            })
            .filter(Boolean);
    };

    useEffect(() => {
        const loadDropdowns = async () => {
            try {
                const [adminsRes, desksRes, affiliatesRes] = await Promise.allSettled([
                    getAdmins(),
                    getAllDesksApi(),
                    getAffiliate(),
                ]);

                if (adminsRes.status === "fulfilled") {
                    const admins = adminsRes.value.data || adminsRes.value;
                    setManagerOptions(
                        (admins || []).map((admin) => ({
                            value: String(admin.id),
                            label: `${admin.first_name} ${admin.last_name}`.trim(),
                        }))
                    );
                }

                if (desksRes.status === "fulfilled") {
                    const desksData = desksRes.value.data || desksRes.value;
                    setDeskOptions(
                        (desksData || []).map((desk) => ({
                            value: String(desk.id),
                            label: `${desk.name || ""} (${desk.description || ""})`.trim(),
                        }))
                    );
                }

                if (affiliatesRes.status === "fulfilled") {
                    const affiliates = affiliatesRes.value.data || affiliatesRes.value;
                    setAffiliatorOptions(
                        (affiliates || []).map((affiliate) => ({
                            value: String(affiliate.id),
                            label:
                                affiliate.first_name || affiliate.last_name
                                    ? `${affiliate.first_name || ""} ${
                                        affiliate.last_name || ""
                                    }`.trim()
                                    : affiliate.email,
                        }))
                    );
                }
            } catch (e) {
                console.error("Error loading dropdowns", e);
            }
        };

        loadDropdowns();
    }, []);

    useEffect(() => {
        const loadFilters = async () => {
            try {
                const token = getToken();

                const endpoints = [
                    `${API_BASE_URL}/common/lead/filters/retention-statuses`,
                    `${API_BASE_URL}/common/lead/filters/sale-statuses`,
                    `${API_BASE_URL}/common/lead/filters/geos`,
                ];

                const [retenRes, saleRes, geoRes] = await Promise.allSettled(
                    endpoints.map((url) =>
                        fetch(url, {
                            method: "GET",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                Accept: "application/json",
                            },
                        })
                    )
                );

                if (saleRes.status === "fulfilled" && saleRes.value.ok) {
                    const json = await saleRes.value.json().catch(() => null);
                    const items = normalizeList(json);
                    setSaleStatusOptions(mapToOptions(items));
                }

                if (retenRes.status === "fulfilled" && retenRes.value.ok) {
                    const json = await retenRes.value.json().catch(() => null);
                    const items = normalizeList(json);
                    setRetenStatusOptions(mapToOptions(items));
                }

                if (geoRes.status === "fulfilled" && geoRes.value.ok) {
                    const json = await geoRes.value.json().catch(() => null);
                    const items = normalizeList(json);
                    setGeoOptions(mapGeoOptions(items, langSelected));
                }
            } catch (e) {
                console.error("Error loading lead filters", e);
            }
        };

        loadFilters();
    }, [langSelected]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleFileChange = (event) => {
        const selected = (event.target.files && event.target.files[0]) || null;
        setFile(selected);
        if (selected) {
            setErrors((prev) => ({ ...prev, file: null, general: null }));
        }
    };

    const triggerFileDownload = (url) => {
        if (!url) return;

        try {
            const link = document.createElement("a");

            const resolvedUrl =
                url.startsWith("http://") || url.startsWith("https://")
                    ? url
                    : new URL(url, window.location.origin).toString();

            link.href = resolvedUrl;

            try {
                const urlObj = new URL(resolvedUrl);
                const pathname = urlObj.pathname || "";
                const filename =
                    pathname.substring(pathname.lastIndexOf("/") + 1) || "import_errors.xlsx";
                link.setAttribute("download", filename);
            } catch (e) {
                link.setAttribute("download", "import_errors.xlsx");
            }

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    const handleSubmit = async () => {
        setErrors({});

        if (!file) {
            setErrors({ file: t.fileRequired });
            return;
        }

        const payload = new FormData();
        payload.append("file", file);

        if (formData.manager_id) payload.append("manager_id", formData.manager_id);
        if (formData.desk_id) payload.append("desk_id", formData.desk_id);
        if (formData.affiliator_id) payload.append("affiliator_id", formData.affiliator_id);
        if (formData.sale_status) payload.append("sale_status", formData.sale_status);
        if (formData.reten_status) payload.append("reten_status", formData.reten_status);
        if (formData.geo) payload.append("geo", formData.geo);

        setIsSubmitting(true);
        try {
            const token = getToken();

            const response = await fetch(`${API_BASE_URL}/admin/lead/leads/import`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
                body: payload,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw errorData || { message: "Import failed" };
            }

            const data = await response.json();

            if (onSuccess) {
                onSuccess(data);
            }

            const errorsFileUrl = data.errorsFileUrl || data.errors_file_url;
            if (errorsFileUrl) {
                triggerFileDownload(errorsFileUrl);
            }
        } catch (err) {
            handleError(err, setErrors);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderSelect = (label, value, options, onChange) => (
        <div className={styles.inputRow}>
            <label>{label}:</label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={{ width: "100%", padding: "8px", borderRadius: 6 }}
            >
                <option value="">{t.selectPlaceholder}</option>
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
        </div>
    );

    const renderGeoSelect = (label, value, options, onChange) => (
        <div className={styles.inputRow}>
            <label>{label}:</label>
            <DropdownSelector
                filterName="geo"
                selectedValue={value}
                handleFilterChange={(_, nextValue) => onChange(nextValue)}
                options={options}
            />
        </div>
    );

    return (
        <div className={styles.modal}>
            {errors.general && <Notification message={errors.general} type={"error"} />}

            <div className={styles.filtersContainer} ref={modalRef}>
                <div className={styles.modalLabel}>
                    <p>{t.title}</p>
                    <div onClick={onClose}>
                        <CloseIcon />
                    </div>
                </div>

                <div className={styles.modalContent}>
                    <div className={styles.inputRow}>
                        <label>{t.fileLabel}:</label>
                        <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} />
                    </div>

                    {errors.file && <div style={{ color: "red", marginTop: 4 }}>{errors.file}</div>}

                    {renderSelect(
                        t.managerLabel,
                        formData.manager_id,
                        managerOptions,
                        (v) => handleFieldChange("manager_id", v)
                    )}
                    {renderSelect(
                        t.deskLabel,
                        formData.desk_id,
                        deskOptions,
                        (v) => handleFieldChange("desk_id", v)
                    )}
                    {renderSelect(
                        t.affiliatorLabel,
                        formData.affiliator_id,
                        affiliatorOptions,
                        (v) => handleFieldChange("affiliator_id", v)
                    )}

                    {renderSelect(
                        t.saleStatusLabel,
                        formData.sale_status,
                        saleStatusOptions,
                        (v) => handleFieldChange("sale_status", v)
                    )}
                    {renderSelect(
                        t.retenStatusLabel,
                        formData.reten_status,
                        retenStatusOptions,
                        (v) => handleFieldChange("reten_status", v)
                    )}
                    {renderGeoSelect(t.geoLabel, formData.geo, geoOptions, (v) =>
                        handleFieldChange("geo", v)
                    )}
                </div>

                <div className={styles.modalFooter}>
                    <div className={styles.confirmationWrapper}>
                        <button
                            className={styles.submitButton}
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting && (
                                <span style={{marginRight: 8, display: "inline-flex"}}>
                                    <LoadingCircle size={16} thickness={2}/>
                                </span>
                            )}
                            {isSubmitting ? t.submitting : t.submit}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
